"use client";

import { useState, useEffect } from "react";
import AppLogo from "@/components/ui/AppLogo";

const sections = [
  { id: "couverture", label: "Couverture" },
  { id: "introduction", label: "Introduction" },
  { id: "plafond", label: "Le Plafond" },
  { id: "piliers", label: "Les Piliers" },
  { id: "matrice", label: "Matrice" },
  { id: "diagnostic", label: "Diagnostic" },
  { id: "solution", label: "Solution" },
];

export default function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("couverture");
  const [menuOpen, setMenuOpen] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);

      // Progress bar
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const currentProgress = (window.scrollY / totalHeight) * 100;
      setProgress(currentProgress);

      // Active section
      const sectionIds = sections.map((s) => s.id);
      for (let i = sectionIds.length - 1; i >= 0; i--) {
        const el = document.getElementById(sectionIds[i]);
        if (el && window.scrollY >= el.offsetTop - 120) {
          setActiveSection(sectionIds[i]);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled
            ? "bg-cream/95 backdrop-blur-md shadow-sm"
            : "bg-transparent"
        }`}
        style={{ borderBottom: scrolled ? "1px solid rgba(28,25,23,0.08)" : "none" }}
      >
        <div className="max-w-8xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          {/* Logo */}
          <a
            href="https://gitezen.pro/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2"
          >
            <AppLogo size={32} text="GîteZen" className="text-charcoal" />
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className={`nav-link text-xs font-bold uppercase tracking-widest transition-colors ${
                  activeSection === s.id
                    ? "text-charcoal active" :"text-warm-gray hover:text-charcoal"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden flex flex-col gap-1.5 p-2"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            <span
              className={`block w-6 h-0.5 bg-charcoal transition-transform duration-300 ${
                menuOpen ? "rotate-45 translate-y-2" : ""
              }`}
            />
            <span
              className={`block w-6 h-0.5 bg-charcoal transition-opacity duration-300 ${
                menuOpen ? "opacity-0" : ""
              }`}
            />
            <span
              className={`block w-6 h-0.5 bg-charcoal transition-transform duration-300 ${
                menuOpen ? "-rotate-45 -translate-y-2" : ""
              }`}
            />
          </button>
        </div>

        {/* Progress bar */}
        <div
          className="progress-bar absolute bottom-0 left-0"
          style={{ width: `${progress}%` }}
        />

        {/* Mobile menu */}
        {menuOpen && (
          <div className="lg:hidden bg-cream border-t border-charcoal/10 px-6 py-6 flex flex-col gap-4">
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className={`text-left text-sm font-bold uppercase tracking-widest py-2 border-b border-charcoal/5 transition-colors ${
                  activeSection === s.id ? "text-sage" : "text-warm-gray"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </nav>
    </>
  );
}